import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  AttachFile as AttachIcon,
  Badge as IdLegalIcon,
  LocalHospital as MedicalIcon,
  School as SchoolIcon,
  AccountBalance as FinancialIcon,
  HealthAndSafety as InsuranceIcon,
  Folder as OtherIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { FamilySelector, useFamilySelection } from '../common';
import { insertFamilyData, updateData } from '../../services/dataApi';
import { uploadMediaFileWeb } from '../../services/api/familypal/mediaUpload';

export interface DocumentData {
  name: string;
  description?: string;
  category: 'id_legal' | 'medical' | 'school' | 'financial' | 'insurance' | 'other';
  tags?: string[];
  file_id?: string;
}

const CATEGORIES: {
  value: DocumentData['category'];
  label: string;
  icon: React.ElementType;
  color: string;
}[] = [
  { value: 'id_legal', label: 'ID & Legal', icon: IdLegalIcon, color: '#5D9CEC' },
  { value: 'medical', label: 'Medical', icon: MedicalIcon, color: '#DC5D72' },
  { value: 'school', label: 'School', icon: SchoolIcon, color: '#EDA745' },
  { value: 'financial', label: 'Financial', icon: FinancialIcon, color: '#A0BC60' },
  { value: 'insurance', label: 'Insurance', icon: InsuranceIcon, color: '#AC8EB8' },
  { value: 'other', label: 'Other', icon: OtherIcon, color: '#9CA3AF' },
];

interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  /** Pass the full family_data item when editing */
  document?: any | null;
}

interface SelectedFile {
  file: File;
  name: string;
  size: number;
  mimeType: string;
}

export const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onClose,
  onSave,
  document,
}) => {
  const isEditing = !!document;
  const existingData: DocumentData | undefined = document?.data?.data;
  const existingDataId: string | undefined = document?.data?.id;

  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<DocumentData['category']>('other');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens/closes or document changes
  useEffect(() => {
    if (open) {
      if (existingData) {
        setName(existingData.name || '');
        setDescription(existingData.description || '');
        setCategory(existingData.category || 'other');
        setTags(existingData.tags || []);
      } else {
        setName('');
        setDescription('');
        setCategory('other');
        setTags([]);
      }
      setSelectedFile(null);
      setTagInput('');
      setError(null);
      setUploadProgress(0);
    }
  }, [open, document]);

  const handleFileSelect = (file: File) => {
    setSelectedFile({
      file,
      name: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
    });
    // Auto-fill name from filename if empty
    if (!name.trim()) {
      const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
      setName(nameWithoutExt);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed]);
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a document name.');
      return;
    }
    if (!isEditing && !selectedFile) {
      setError('Please select a file to upload.');
      return;
    }
    if (selectedFamilyIds.length === 0 && !isEditing) {
      setError('No family found. Please try again.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const docData: DocumentData = {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        tags: tags.length > 0 ? tags : undefined,
        file_id: existingData?.file_id,
      };

      if (selectedFile) {
        // Step 1: Create/update the data record to get a data ID
        let dataId: string;

        if (isEditing && existingDataId) {
          dataId = existingDataId;
        } else {
          // Insert a placeholder record first
          const result = await insertFamilyData(selectedFamilyIds[0], 'document', docData);
          if (!result?.id) throw new Error('Failed to create document record');
          dataId = result.id;
        }

        setUploadProgress(5);

        // Step 2: Upload file using web media upload helper
        const fileForUpload = {
          uri: URL.createObjectURL(selectedFile.file),
          name: selectedFile.name,
          mimeType: selectedFile.mimeType,
          size: selectedFile.size,
        };

        const uploaded = await uploadMediaFileWeb(fileForUpload, dataId, (progress) => {
          setUploadProgress(5 + Math.floor(progress * 0.9));
        });

        // Revoke object URL to free memory
        URL.revokeObjectURL(fileForUpload.uri);

        // Step 3: Update record with file_id
        const finalData: DocumentData = { ...docData, file_id: uploaded.id };
        await updateData(dataId, 'document', finalData);
        setUploadProgress(100);
      } else if (isEditing && existingDataId) {
        // Metadata-only update
        await updateData(existingDataId, 'document', docData);
      }

      await onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save document. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open={open} onClose={!uploading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isEditing ? 'Edit Document' : 'Upload Document'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* File Drop Zone */}
          <Grid size={12}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <Paper
              variant="outlined"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !selectedFile && fileInputRef.current?.click()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: selectedFile ? 'default' : 'pointer',
                borderStyle: 'dashed',
                borderColor: isDragOver ? 'primary.main' : selectedFile ? 'success.main' : 'divider',
                borderWidth: 2,
                bgcolor: isDragOver
                  ? 'action.hover'
                  : selectedFile
                  ? 'success.50'
                  : 'background.default',
                transition: 'all 0.2s',
                '&:hover': !selectedFile
                  ? { borderColor: 'primary.main', bgcolor: 'action.hover' }
                  : {},
              }}
            >
              {selectedFile ? (
                <Box>
                  <AttachIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      Remove
                    </Button>
                    <Button
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Replace
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {isDragOver ? 'Drop file here' : 'Drag & drop or click to select'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {isEditing
                      ? 'Leave empty to keep existing file'
                      : 'PDF, images, Word, Excel, and more'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Upload Progress */}
          {uploading && (
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {selectedFile ? `Uploading... ${uploadProgress}%` : 'Saving...'}
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Grid>
          )}

          {/* Name */}
          <Grid size={12}>
            <TextField
              label="Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder="e.g., John's Passport"
              disabled={uploading}
            />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          {/* Description */}
          <Grid size={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description..."
              disabled={uploading}
            />
          </Grid>

          {/* Category */}
          <Grid size={12}>
            <FormControl fullWidth disabled={uploading}>
              <InputLabel>Category *</InputLabel>
              <Select
                value={category}
                label="Category *"
                onChange={(e) => setCategory(e.target.value as DocumentData['category'])}
              >
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Icon sx={{ fontSize: 20, color: cat.color }} />
                        {cat.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Tags */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', gap: 1, mb: tags.length > 0 ? 1 : 0 }}>
              <TextField
                label="Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                size="small"
                placeholder="Add a tag and press Enter"
                sx={{ flex: 1 }}
                disabled={uploading}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddTag}
                disabled={!tagInput.trim() || uploading}
                sx={{ minWidth: 40, px: 1 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            {tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                    disabled={uploading}
                    sx={{
                      bgcolor: '#EEF2FF',
                      color: '#4F46E5',
                      '& .MuiChip-deleteIcon': { color: '#4F46E5' },
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={uploading}
          startIcon={uploading ? undefined : <UploadIcon />}
        >
          {uploading ? 'Saving...' : isEditing ? 'Save Changes' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUploadDialog;
